// EventsRegistration.jsx
import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import FloatingLabelInput from "../Generic/FloatingLabelInput";
import LoadingSpinner from "../Generic/LoadingSpinner";
import { createOrder, registerForEvent } from "../../api/API";
import { getCurrentUser, isLoggedIn } from "../../utility/UserUtils";

const EventsRegistration = ({ show, onHide, event }) => {
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);

  // Load current user
  useEffect(() => {
    if (isLoggedIn()) {
      setUser(getCurrentUser());
    }
  }, [show]);

  // Robust check for existing registration
  const isAlreadyRegistered = event?.attendees?.some((att) => {
    const memberId = att.member?.id || att.member?._id || att.member || "";
    return memberId.toString() === user?.id?.toString();
  });

  // Check if event is full
  const isEventFull = () => {
    if (!event.capacity) return false; // no capacity limit
    return event.attendeesCount >= event.capacity;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      toast.info("You must login to register!");
      window.location.href = "/login";
      return;
    }

    if (isAlreadyRegistered) {
      toast.info("You have already registered for this event.");
      return;
    }

    setLoading(true);

    try {
      if (event.price > 0) {
        // Paid event → Razorpay flow
        openRazorpay(event.id, event.price, event.name);
      } else {
        // Free event → directly call verifyPayment endpoint
        const res = await registerForEvent({}, event.id, user);
        if (res.success) {
          toast.success("Registered successfully!");
          onHide();
        } else {
          toast.error(res.error || "Registration failed!");
        }
      }
    } catch (err) {
      console.error("Registration failed:", err);
      toast.error("Registration failed!");
    } finally {
      setLoading(false);
    }
  };

  const openRazorpay = async (eventId, amount, eventName) => {
    setLoading(true);
    try {
      const order = await createOrder({ eventId, amount });

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY,
        amount: order.amount,
        currency: order.currency,
        name: "WISE Events",
        description: `Registration for ${eventName}`,
        order_id: order.id,
        handler: async (response) => {
          setLoading(true);
          try {
            const data = await registerForEvent(
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
              eventId,
              user
            );

            if (data.success) {
              toast.success("Payment successful & Registered!");
              onHide();
            } else {
              toast.error(
                data.error || "Payment verified but registration failed"
              );
            }
          } catch (err) {
            console.error("Payment verification failed:", err);
            toast.error("Payment succeeded but registration failed.");
          } finally {
            setLoading(false);
          }
        },
        prefill: {
          name:
            user.firstName && user.lastName
              ? `${user.firstName} ${user.lastName}`
              : user.name || "",
          email: user.email,
        },
        theme: { color: "#3399cc" },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        console.error("Payment failed:", response);
        toast.error("Payment failed. Please try again.");
      });
      rzp.open();
    } catch (err) {
      console.error("Failed to create order:", err);
      toast.error("Could not initiate payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <div className="loading-overlay">
          <LoadingSpinner text="Processing..." />
        </div>
      )}
      <Modal show={show} onHide={onHide} centered>
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            {event.price > 0 ? "Pay & Register" : "Register for Event"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-light">
          <form onSubmit={handleSubmit} autoComplete="off">
            <FloatingLabelInput
              label="Name"
              type="text"
              value={
                user?.firstName
                  ? `${user.firstName} ${user.lastName}`
                  : user?.name || ""
              }
              readOnly
            />
            <FloatingLabelInput
              label="Email"
              type="email"
              value={user?.email || ""}
              readOnly
            />
            <FloatingLabelInput
              label="Cohort"
              type="text"
              value={
                typeof user?.cohort === "object"
                  ? user?.cohort?.title
                  : user?.cohort || "N/A"
              }
              readOnly
            />
            {event.price > 0 && <p className="mt-3">💰 Fee: ₹{event.price}</p>}
            {event.capacity && (
              <p className="mt-2">
                Capacity: {event.attendeesCount}/{event.capacity}
              </p>
            )}
            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button variant="secondary" onClick={onHide} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading || isAlreadyRegistered || isEventFull()}
              >
                {isAlreadyRegistered
                  ? "Already Registered"
                  : isEventFull()
                  ? "Event Full"
                  : event.price > 0
                  ? "Pay & Register"
                  : "Register"}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default EventsRegistration;
